import { pubsub } from '@services';

//#region src/array/last.ts
/**
* Returns the last element of an array.
*
* This function takes an array and returns the last element of the array.
* If the array is empty, the function returns `undefined`.
*
* Unlike some implementations, this function is optimized for performance
* by directly accessing the last index of the array.
*
* @template T - The type of elements in the array.
* @param arr - The array from which to get the last element.
* @returns The last element of the array, or `undefined` if the array is empty.
*
* @example
* const arr = [1, 2, 3];
* const lastElement = last(arr);
* // lastElement will be 3
*
* const emptyArr: number[] = [];
* const noElement = last(emptyArr);
* // noElement will be undefined
*/
function last(arr) {
	return arr[arr.length - 1];
}

//#region src/predicate/isPlainObject.ts
/**
* Checks if a given value is a plain object.
*
* @param value - The value to check.
* @returns True if the value is a plain object, otherwise false.
*
* @example
* ```typescript
* // ✅👇 True
*
* isPlainObject({ });                       // ✅
* isPlainObject({ key: 'value' });          // ✅
* isPlainObject({ key: new Date() });       // ✅
* isPlainObject(new Object());              // ✅
* isPlainObject(Object.create(null));       // ✅
* isPlainObject({ nested: { key: true} });  // ✅
* isPlainObject(new Proxy({}, {}));         // ✅
* isPlainObject({ [Symbol('tag')]: 'A' });  // ✅
*
* // ✅👇 (cross-realms, node context, workers, ...)
* const runInNewContext = await import('node:vm').then(
*     (mod) => mod.runInNewContext
* );
* isPlainObject(runInNewContext('({})'));   // ✅
*
* // ❌👇 False
*
* class Test { };
* isPlainObject(new Test())           // ❌
* isPlainObject(10);                  // ❌
* isPlainObject(null);                // ❌
* isPlainObject('hello');             // ❌
* isPlainObject([]);                  // ❌
* isPlainObject(new Date());          // ❌
* isPlainObject(new Uint8Array([1])); // ❌
* isPlainObject(Buffer.from('ABC'));  // ❌
* isPlainObject(Promise.resolve({})); // ❌
* isPlainObject(Object.create({}));   // ❌
* isPlainObject(new (class Cls {}));  // ❌
* isPlainObject(globalThis);          // ❌,
* ```
*/
function isPlainObject(value) {
	if (!value || typeof value !== "object") return false;
	const proto = Object.getPrototypeOf(value);
	if (!(proto === null || proto === Object.prototype || Object.getPrototypeOf(proto) === null)) return false;
	return Object.prototype.toString.call(value) === "[object Object]";
}

//#region src/string/capitalize.ts
/**
* Converts the first character of string to upper case and the remaining to lower case.
*
* @template T - Literal type of the string.
* @param str - The string to be converted to uppercase.
* @returns The capitalized string.
*
* @example
* const result = capitalize('fred') // returns 'Fred'
* const result2 = capitalize('FRED') // returns 'Fred'
*/
function capitalize(str) {
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

//#region src/string/words.ts
/**
* Regular expression pattern to split strings into words for various case conversions
*
* This pattern matches sequences of characters in a string, considering the following cases:
* - Sequences of two or more uppercase letters followed by an uppercase letter and lowercase letters or digits (for acronyms)
* - Sequences of one uppercase letter optionally followed by lowercase letters and digits
* - Single uppercase letters
* - Sequences of digits
* - Emojis and other Unicode characters
*
* The resulting match can be used to convert camelCase, snake_case, kebab-case, and other mixed formats into
* a consistent format like snake case. It also supports emojis and other Unicode characters.
*
* @example
* const matches = 'camelCaseHTTPRequest🚀'.match(CASE_SPLIT_PATTERN);
* // matches: ['camel', 'Case', 'HTTP', 'Request', '🚀']
*/
const CASE_SPLIT_PATTERN = /\p{Lu}?\p{Ll}+|[0-9]+|\p{Lu}+(?!\p{Ll})|\p{Emoji_Presentation}|\p{Extended_Pictographic}|\p{L}+/gu;
/**
* Splits `string` into an array of its words, treating spaces and punctuation marks as separators.
*
* @param str The string to inspect.
* @param [pattern] The pattern to match words.
* @returns Returns the words of `string`.
*
* @example
* words('fred, barney, & pebbles');
* // => ['fred', 'barney', 'pebbles']
*
* words('camelCaseHTTPRequest🚀');
* // => ['camel', 'Case', 'HTTP', 'Request', '🚀']
*
* words('Lunedì 18 Set')
* // => ['Lunedì', '18', 'Set']
*/
function words(str) {
	return Array.from(str.match(CASE_SPLIT_PATTERN) ?? []);
}

//#region src/string/camelCase.ts
/**
* Converts a string to camel case.
*
* Camel case is the naming convention in which the first word is written in lowercase and
* each subsequent word begins with a capital letter, concatenated without any separator characters.
*
* @param str - The string that is to be changed to camel case.
* @returns The converted string to camel case.
*
* @example
* const convertedStr1 = camelCase('camelCase') // returns 'camelCase'
* const convertedStr2 = camelCase('some whitespace') // returns 'someWhitespace'
* const convertedStr3 = camelCase('hyphen-text') // returns 'hyphenText'
* const convertedStr4 = camelCase('HTTPRequest') // returns 'httpRequest'
* const convertedStr5 = camelCase('Keep unicode 😅') // returns 'keepUnicode😅'
*/
function camelCase(str) {
	const words$1 = words(str);
	if (words$1.length === 0) return "";
	const [first, ...rest] = words$1;
	return `${first.toLowerCase()}${rest.map((word) => capitalize(word)).join("")}`;
}

//#region src/compat/predicate/isArray.ts
/**
* Checks if the given value is an array.
*
* This function tests whether the provided value is an array or not.
* It returns `true` if the value is an array, and `false` otherwise.
*
* This function can also serve as a type predicate in TypeScript, narrowing the type of the argument to an array.
*
* @param value - The value to test if it is an array.
* @returns `true` if the value is an array, `false` otherwise.
*
* @example
* const value1 = [1, 2, 3];
* const value2 = 'abc';
* const value3 = () => {};
*
* console.log(isArray(value1)); // true
* console.log(isArray(value2)); // false
* console.log(isArray(value3)); // false
*/
function isArray(value) {
	return Array.isArray(value);
}

//#region src/object/toCamelCaseKeys.ts
/**
* Creates a new object composed of the properties with keys converted to camelCase.
*
* This function takes an object and returns a new object that includes the same properties,
* but with all keys converted to camelCase format.
*
* @template T - The type of object.
* @param obj - The object to convert keys from.
* @returns A new object with all keys converted to camelCase.
*
* @example
* // Example with objects
* const obj = { user_id: 1, first_name: 'John' };
* const result = toCamelCaseKeys(obj);
* // result will be { userId: 1, firstName: 'John' }
*
* // Example with arrays of objects
* const arr = [
*   { user_id: 1, first_name: 'John' },
*   { user_id: 2, first_name: 'Jane' }
* ];
* const arrResult = toCamelCaseKeys(arr);
* // arrResult will be [{ userId: 1, firstName: 'John' }, { userId: 2, firstName: 'Jane' }]
*
* // Example with nested objects
* const nested = {
*   user_data: {
*     user_id: 1,
*     user_address: {
*       street_name: 'Main St',
*       zip_code: '12345'
*     }
*   }
* };
* const nestedResult = toCamelCaseKeys(nested);
* // nestedResult will be:
* // {
* //   userData: {
* //     userId: 1,
* //     userAddress: {
* //       streetName: 'Main St',
* //       zipCode: '12345'
* //     }
* //   }
* // }
*/
function toCamelCaseKeys(obj) {
	if (isArray(obj)) return obj.map((item) => toCamelCaseKeys(item));
	if (isPlainObject(obj)) {
		const result = {};
		const keys = Object.keys(obj);
		for (let i = 0; i < keys.length; i++) {
			const key = keys[i];
			const camelKey = camelCase(key);
			result[camelKey] = toCamelCaseKeys(obj[key]);
		}
		return result;
	}
	return obj;
}

/**
 * Fetch HTML from a URL
 * @param {string} url - The URL to fetch HTML from
 * @returns {Promise<Document>} - The HTML document
 */
async function fetchHTML(url) {
  const response = await fetch(url);
  return new DOMParser().parseFromString(await response.text(), 'text/html');
}

/**
 * Custom element that implements infinite scrolling functionality
 * Observes the last item in a list and loads more content when it becomes visible
 */
class InfiniteLoad extends HTMLElement {
  liClassList = ['animate-pulse', 'bg-neutral-200', 'aspect-[413/697.1]'];
  unsubscribers = [];

  connectedCallback() {
    this.initState();

    this.unsubscribers.push(
      pubsub.subscribe('load-more:clicked', () => this.initIntersectionObserver())
    );
    this.unsubscribers.push(pubsub.subscribe('collection:filter:fetched', () => this.resetState()));
  }

  disconnectedCallback() {
    this.unsubscribers.forEach((unsubscribe) => unsubscribe());
    this.observer?.disconnect();
  }

  /**
   * Initialize state from DOM
   */
  initState() {
    this.paginate = toCamelCaseKeys(JSON.parse(this.dataset.paginate));
    this.lastPageSize = this.paginate.items % this.paginate.pageSize || this.paginate.pageSize;
    this.currentItems = this.paginate.pageSize;
    this.itemsList = this.querySelector('[data-infinite-load-grid]');
    this.items = [...this.itemsList.querySelectorAll('li:not([data-promo-block])')];
    this.observerTarget = last(this.items);
  }

  /**
   * Reset state when filters change
   */
  resetState() {
    this.observer?.disconnect();
    this.observer = null;
    this.initState();
  }

  initIntersectionObserver() {
    this.observer = new IntersectionObserver(
      ([{ isIntersecting }]) => {
        if (!isIntersecting) return;
        // Unobserve the previous observer target
        this.observer.unobserve(this.observerTarget);

        // Increment the current page
        this.paginate.currentPage++;

        // If the current page is greater than the total pages, return
        if (this.paginate.currentPage > this.paginate.pages) return;

        // Create the new list items
        const listItems = this.createListItems();
        this.itemsList.append(...listItems);

        // Fetch the new page
        this.fetchPage();

        // Set the new observer target
        this.observerTarget = last(listItems);

        // Observe the new observer target
        this.observer.observe(this.observerTarget);
      },
      {
        root: null,
        threshold: 0,
      }
    );

    this.observer.observe(this.observerTarget);
  }

  createListItems() {
    const pageSize =
      this.paginate.currentPage === this.paginate.pages
        ? this.lastPageSize
        : this.paginate.pageSize;
    const listItems = [];
    for (let i = 0; i < pageSize; i++) {
      listItems.push(this.createListItem());
    }
    return listItems;
  }

  createListItem() {
    this.currentItems++;
    const li = document.createElement('li');
    li.id = `product-card--${this.currentItems}`;
    li.classList.add('flex', ...this.liClassList);
    return li;
  }

  async fetchPage() {
    const url = new URL(window.location.href);
    url.searchParams.set('page', this.paginate.currentPage);

    try {
      const html = await fetchHTML(url);
      this.appendNewProducts(html);
      pubsub.publish('infinite-load:fetched', {
        productsSize: this.currentItems,
      });
    } catch (error) {
      console.error('Failed to fetch more content:', error);
    }
  }

  /**
   * Appends new products to the list and updates the last item reference
   * @param {Document} html - The HTML document to append the new products to
   */
  appendNewProducts(html) {
    const products = html.querySelectorAll(
      '[data-infinite-load-grid] > li:not([data-promo-block])'
    );

    products.forEach((product) => {
      const li = document.getElementById(product.id);
      li.innerHTML = product.innerHTML;
      li.classList.remove(...this.liClassList);

      const images = li.querySelectorAll('img');
      images.forEach((image) => {
        image.style.removeProperty('display');
        image.style.removeProperty('visibility');
      });
    });
  }
}

customElements.define('infinite-load', InfiniteLoad);
